import type { ContextQuestion, VisibilityModelRule } from '../../data/schemas';
import {
  VISIBILITY_MODEL_COPY,
  VISIBILITY_MODEL_FALLBACK_NOTICE,
} from '../../data/visibilityModelCopy';
import { VISIBILITY_MODEL_IDS, type VisibilityModelId } from '../../types/ids';
import type { EvaluationContext, VisibilityModelResult } from '../../types/result';
import { evaluateConditionGroup } from '../tensions/evaluateRuleCondition';

function isVisibilityModelId(value: string): value is VisibilityModelId {
  return (VISIBILITY_MODEL_IDS as readonly string[]).includes(value);
}

/**
 * Bestimmt das empfohlene Sichtbarkeitsmodell (Abschnitt 20). Erste zutreffende Regel gewinnt.
 * Passt keine Regel, wird das vom Nutzer gewählte Modell transparent mit Hinweis gezeigt (ERR-007).
 */
export function determineVisibilityModel(
  context: EvaluationContext,
  rules: readonly VisibilityModelRule[],
  contextQuestions: readonly ContextQuestion[],
): VisibilityModelResult {
  const sorted = [...rules].sort((a, b) => b.priority - a.priority);
  const match = sorted.find((rule) => evaluateConditionGroup(rule.conditions, 'all', context));

  if (match) {
    return {
      model: match.result.model,
      label: match.result.label,
      summary: VISIBILITY_MODEL_COPY[match.result.model].summary,
      isFallback: false,
    };
  }

  const chosenValue = context.context.visibilityModel;
  const field = contextQuestions.find((question) => question.id === 'visibilityModel');
  const chosenOption = field?.options.find((option) => option.value === chosenValue);
  const label = chosenOption?.label ?? 'Kein Sichtbarkeitsmodell ausgewählt';
  const model = chosenValue && isVisibilityModelId(chosenValue) ? chosenValue : null;

  return {
    model,
    label,
    summary: VISIBILITY_MODEL_FALLBACK_NOTICE,
    isFallback: true,
  };
}
